import {
  ApiAttributionModel,
  ApiChartYAxisKey,
  ApiDimension,
  ApiMetric,
  ApiProject,
} from "@incendium/api";
import { IChart, IChartData, TChartData } from "Interfaces";
import { IFromToChartOutput } from "Providers/FromToProvider";
import { getPrimaryAnalytics } from "../services/analyticsService";
import produce from "immer";
import { isEqual } from "lodash";
import { useState, useRef, useCallback } from "react";
import { useDebounce } from "react-use";
import { useAttributionContext } from "Providers/AttributionProvider";
import { replaceAttributionMetricsInChart } from "../utils/utils";
import { isAttributionMetric } from "../services/attributionMapping";

const getQueryRelatedFields = (chart: IChart) => {
  return {
    dimension: chart.dimension,
    orderBy: chart.orderBy,
    limit: chart.limit,
    attributes: chart.attributes,
    ranks: chart.ranks,
    having: chart.having,
    crawler: chart.crawler,
    includeSubdomains: chart.includeSubdomains,
    includeEmpty: chart.includeEmpty,
    includePublisherReadyOnly: chart.includePublisherReadyOnly,
    withTotals: chart.displayOptions?.showTotals,
    metrics: chart.yAxisKeys.map((y) => (y as ApiChartYAxisKey).fields).flat(),
    dateDimension: chart.dateDimension,
  };
};

const chartHasAttributionMetrics = (chart: IChart): boolean => {
  const metrics = chart.yAxisKeys
    .map((y) => (y as ApiChartYAxisKey).fields)
    .flat()
    .filter((m) => m !== "");

  return metrics.some((metric) => isAttributionMetric(metric as ApiMetric));
};

// Custom hook
const useChartData = (
  selectedProject: ApiProject | null,
  chart: IChart,
  chartOutput: IFromToChartOutput | null,
  opts?: {
    ignoreModel?: boolean;
    comparisonFrom?: Date;
    comparisonTo?: Date;
  },
) => {
  const [chartData, setChartData] = useState<IChartData | null>(null);
  const [comparisonChartData, setComparisonChartData] =
    useState<IChartData | null>(null);
  const [totalsData, setTotalsData] = useState<TChartData>();
  const [comparisonTotalsData, setComparisonTotalsData] =
    useState<TChartData>();
  const [dimensions, setDimensions] = useState<ApiDimension[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const previousChartOutputRef = useRef<IFromToChartOutput | null>(null);
  const previousChartRef = useRef<IChart | null>(null);
  const previousModelRef = useRef<ApiAttributionModel | null>(null);
  const previousComparisonDatesRef = useRef<{
    comparisonFrom?: Date;
    comparisonTo?: Date;
  }>({});
  const loadRequestIdRef = useRef(0);

  const { model } = useAttributionContext();
  const { ignoreModel, comparisonFrom, comparisonTo } = opts || {};

  const invalidateInFlightLoads = useCallback(() => {
    loadRequestIdRef.current += 1;
    setLoading(false);
  }, []);

  const clearData = useCallback(() => {
    setChartData(null);
    setComparisonChartData(null);
    setTotalsData(undefined);
    setComparisonTotalsData(undefined);
    setDimensions([]);
  }, []);

  const loadData = useCallback(
    async (
      projectId: number,
      chart: IChart,
      from?: Date,
      to?: Date,
      lastNDays?: number,
      model?: ApiAttributionModel,
      comparisonFrom?: Date,
      comparisonTo?: Date,
    ) => {
      const requestId = ++loadRequestIdRef.current;
      setLoading(true);
      setError(undefined);

      const effectiveModel = ignoreModel ? undefined : model;

      try {
        const res = await getPrimaryAnalytics(
          projectId,
          { ...chart, withTotals: chart.displayOptions?.showTotals },
          from,
          to,
          lastNDays,
          effectiveModel,
          comparisonFrom,
          comparisonTo,
        );

        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        setDimensions([...res.dimensions] as ApiDimension[]);

        // If the metric is an attribution metric such as MetricRevenue,
        // the backend passes the correct metric for the selected model
        const yAxisKeys = effectiveModel
          ? replaceAttributionMetricsInChart(chart, [effectiveModel]).yAxisKeys
          : chart.yAxisKeys;

        setChartData({
          data: res.chartData,
          y: yAxisKeys,
        });
        setTotalsData(res.totals);

        // Handle comparison data from the same API response
        if (res.comparisonChartData) {
          setComparisonChartData({
            data: res.comparisonChartData,
            y: yAxisKeys,
          });
          setComparisonTotalsData(res.comparisonTotals);
        } else {
          setComparisonChartData(null);
          setComparisonTotalsData(undefined);
        }

        setLoading(false);
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        setLoading(false);
        setError(error);
      }
    },
    [ignoreModel],
  );

  useDebounce(
    () => {
      // Early return if no project or chart output
      if (!selectedProject?.id || !chartOutput) {
        invalidateInFlightLoads();
        clearData();
        previousChartOutputRef.current = chartOutput;
        return;
      }

      const queryFields = getQueryRelatedFields(chart);
      const hasMetrics =
        (queryFields.metrics || []).filter((m) => m !== "").length > 0;

      // Clear chart data if no metrics
      if (!hasMetrics) {
        invalidateInFlightLoads();
        setChartData((old) =>
          produce(old, (draft) => {
            if (!draft) return;
            draft.data = [];
            draft.y = [];
          }),
        );
        setComparisonChartData((old) =>
          produce(old, (draft) => {
            if (!draft) return;
            draft.data = [];
            draft.y = [];
          }),
        );
        return;
      }

      // Check what has changed to decide if we need to reload data
      const hasAttributionMetrics = chartHasAttributionMetrics(chart);
      const modelChanged =
        !ignoreModel &&
        hasAttributionMetrics &&
        previousModelRef.current !== model;
      const comparisonDatesChanged = !isEqual(
        previousComparisonDatesRef.current,
        { comparisonFrom, comparisonTo },
      );
      const dateRangeChanged = !isEqual(
        previousChartOutputRef.current,
        chartOutput,
      );
      const queryFieldsChanged =
        !previousChartRef.current ||
        !isEqual(
          getQueryRelatedFields(chart),
          getQueryRelatedFields(previousChartRef.current),
        );
      const yAxisKeysChanged =
        previousChartRef.current &&
        !isEqual(previousChartRef.current.yAxisKeys, chart.yAxisKeys);

      // If only yAxisKeys changed (and nothing else), just update the y-axis without reloading data
      if (
        !modelChanged &&
        !comparisonDatesChanged &&
        !dateRangeChanged &&
        !queryFieldsChanged &&
        yAxisKeysChanged
      ) {
        setChartData((old) =>
          produce(old, (draft) => {
            if (!draft) return;
            draft.y = chart.yAxisKeys;
          }),
        );
        setComparisonChartData((old) =>
          produce(old, (draft) => {
            if (!draft) return;
            draft.y = chart.yAxisKeys;
          }),
        );
        previousChartRef.current = chart;
        return;
      }

      // If nothing relevant changed, skip the reload
      if (
        !modelChanged &&
        !comparisonDatesChanged &&
        !dateRangeChanged &&
        !queryFieldsChanged &&
        !yAxisKeysChanged &&
        previousChartRef.current
      ) {
        return;
      }

      // Reload data
      clearData();
      loadData(
        selectedProject.id,
        chart,
        chartOutput.from,
        chartOutput.to,
        chartOutput.lastNDays,
        model || undefined,
        comparisonFrom,
        comparisonTo,
      );

      // Update refs
      previousChartOutputRef.current = chartOutput;
      previousChartRef.current = chart;
      previousModelRef.current = model;
      previousComparisonDatesRef.current = { comparisonFrom, comparisonTo };
    },
    100,
    [
      chart,
      selectedProject,
      chartOutput,
      loadData,
      model,
      ignoreModel,
      comparisonFrom,
      comparisonTo,
      clearData,
      invalidateInFlightLoads,
    ],
  );

  return {
    chartData,
    comparisonChartData,
    dimensions,
    loading,
    error,
    totalsData,
    comparisonTotalsData,
  };
};

export default useChartData;
